import api from './api';

/**
 * Network Service
 * Handles all network/social-related API calls (follow, likes, etc.)
 */
const networkService = {
    // ==================== Network Feed ====================
    
    /**
     * Get network offers feed
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getFeed: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/network/feed${queryString ? `?${queryString}` : ''}`);
    },

    // ==================== Follow ====================
    
    /**
     * Follow a user
     * @param {string} userId - User ID to follow
     * @returns {Promise<Object>}
     */
    follow: async (userId) => {
        return api.post(`/user/network/follow/${userId}`);
    },

    /**
     * Unfollow a user
     * @param {string} userId - User ID to unfollow
     * @returns {Promise<Object>}
     */
    unfollow: async (userId) => {
        return api.delete(`/user/network/unfollow/${userId}`);
    },

    /**
     * Get followers
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getFollowers: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/network/followers${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get following
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getFollowing: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/network/following${queryString ? `?${queryString}` : ''}`);
    },

    // ==================== Likes ====================
    
    /**
     * Like an offer
     * @param {string} offerId - Offer ID
     * @returns {Promise<Object>}
     */
    likeOffer: async (offerId) => {
        return api.post(`/user/network/offers/${offerId}/like`);
    },

    /**
     * Unlike an offer
     * @param {string} offerId - Offer ID
     * @returns {Promise<Object>}
     */
    unlikeOffer: async (offerId) => {
        return api.delete(`/user/network/offers/${offerId}/unlike`);
    },

    /**
     * Get liked offers
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getLikedOffers: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/network/likes${queryString ? `?${queryString}` : ''}`);
    },

    // ==================== Comments ====================
    
    /**
     * Add comment to offer
     * @param {string} offerId - Offer ID
     * @param {string} text - Comment text
     * @returns {Promise<Object>}
     */
    addComment: async (offerId, text) => {
        return api.post(`/user/network/offers/${offerId}/comments`, { text });
    },

    /**
     * Delete comment
     * @param {string} commentId - Comment ID
     * @returns {Promise<Object>}
     */
    deleteComment: async (commentId) => {
        return api.delete(`/user/network/comments/${commentId}`);
    },

    /**
     * Get offer comments
     * @param {string} offerId - Offer ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getComments: async (offerId, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/network/offers/${offerId}/comments${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Reply to a comment
     * @param {string} commentId - Comment ID
     * @param {string} text - Reply text
     * @returns {Promise<Object>}
     */
    replyToComment: async (commentId, text) => {
        return api.post(`/user/network/comments/${commentId}/reply`, { text });
    },

    /**
     * Like a comment
     * @param {string} commentId - Comment ID
     * @returns {Promise<Object>}
     */
    likeComment: async (commentId) => {
        return api.post(`/user/network/comments/${commentId}/like`);
    },

    /**
     * Unlike a comment
     * @param {string} commentId - Comment ID
     * @returns {Promise<Object>}
     */
    unlikeComment: async (commentId) => {
        return api.delete(`/user/network/comments/${commentId}/unlike`);
    },

    // ==================== Copy Offer ====================
    
    /**
     * Copy offer to my offers
     * @param {string} offerId - Offer ID
     * @returns {Promise<Object>}
     */
    copyToMyOffers: async (offerId) => {
        return api.post(`/user/network/offers/${offerId}/copy`);
    },

    // ==================== Tracking ====================
    
    /**
     * Track view on an offer
     * @param {string} offerId - Offer ID
     * @returns {Promise<Object>}
     */
    trackView: async (offerId) => {
        return api.post(`/user/network/offers/${offerId}/view`);
    },

    /**
     * Track share on an offer
     * @param {string} offerId - Offer ID
     * @returns {Promise<Object>}
     */
    trackShare: async (offerId) => {
        return api.post(`/user/network/offers/${offerId}/share`);
    },

    // ==================== User Profile ====================
    
    /**
     * Get user public profile
     * @param {string} userId - User ID
     * @returns {Promise<Object>}
     */
    getUserProfile: async (userId) => {
        return api.get(`/user/network/users/${userId}`);
    },

    /**
     * Get user offers
     * @param {string} userId - User ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getUserOffers: async (userId, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/network/users/${userId}/offers${queryString ? `?${queryString}` : ''}`);
    },

    // ==================== Network Requests ====================
    
    /**
     * Get network requests feed
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getNetworkRequests: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/network/requests${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get network counts (all offers, following offers, requests)
     * @returns {Promise<Object>}
     */
    getNetworkCounts: async () => {
        return api.get('/user/network/counts');
    },

    /**
     * Get public requests for a specific marketer (no auth)
     * @param {string} marketerId - Marketer user ID
     * @param {Object} params - Query parameters (page, limit)
     * @returns {Promise<Object>}
     */
    getMarketerRequests: async (marketerId, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';
        const response = await fetch(`${API_BASE_URL}/public/marketers/${marketerId}/requests${queryString ? `?${queryString}` : ''}`);
        return response.json();
    },

    /**
     * Check if current user is following a specific user
     * @param {string} userId
     * @returns {Promise<Object>}
     */
    checkIsFollowing: async (userId) => {
        return api.get(`/user/network/users/${userId}`);
    },

    /**
     * Get public marketer profile by username (no auth)
     * @param {string} username - Marketer username
     * @returns {Promise<Object>}
     */
    getMarketerProfile: async (username) => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';
        const response = await fetch(`${API_BASE_URL}/public/marketers/${username}/profile`);
        if (!response.ok) throw new Error('marketer_not_found');
        const data = await response.json();
        return data;
    },

    /**
     * Submit a visitor request from a public marketer page (no auth)
     * @param {string} username - Marketer username
     * @param {Object} data - Visitor request fields
     * @returns {Promise<Object>}
     */
    submitMarketerRequest: async (username, data) => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';
        const response = await fetch(`${API_BASE_URL}/public/marketers/${username}/requests`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.message || 'تعذر إرسال الطلب. حاول مرة أخرى.');
        }
        return result;
    },
};

export default networkService;
